// The fiche client — pure rules (PMS-CARDEX-BRIEF §6.1 S3–S5).
//
// The CLIENT MIRROR of `pms_save_guest_sheet` and the table's CHECKs
// (20260929100100). The database is the enforcer; this exists so the desk sees
// « SIREN invalide » under the field before pressing Enregistrer, not as a
// sentence back from a round trip. Both sides implement the same three rules,
// and the tests pin the same cases the migration's self-check does.

import type { GuestSheet, GuestSheetPatch } from './types.js';

/** The fields Vega holds while `vegaHeld` (S3) — refused by the RPC when
 *  they change, and read-only on screen. Everything else is the PMS's (S4). */
export const PMS_GUEST_IDENTITY_FIELDS = [
  'lastName',
  'firstName',
  'email',
  'phone',
  'mobile',
  'addressLine1',
  'addressLine2',
  'postalCode',
  'city',
  'countryCode',
] as const satisfies readonly (keyof GuestSheetPatch)[];

export const PMS_GUEST_OWNED_FIELDS = [
  'lang',
  'notes',
  'customerType',
  'customerSubtype',
  'siren',
  'vatNumber',
] as const satisfies readonly (keyof GuestSheetPatch)[];

export const PMS_CUSTOMER_TYPE_LABELS_FR = {
  particulier: 'Particulier',
  societe: 'Société',
} as const;

/** Spaces out — the way a SIREN is usually written (« 843 941 931 »). */
export const pmsNormaliseSiren = (s: string): string => s.replace(/\s+/g, '');

/** Spaces out, upper case — « fr 93 843941931 » → « FR93843941931 ». */
export const pmsNormaliseVatNumber = (v: string): string => v.replace(/\s+/g, '').toUpperCase();

/** Nine digits and INSEE's Luhn key; La Poste (356 000 000) is the documented
 *  exception. Mirrors `pms_siren_valid`. */
export function pmsSirenValid(siren: string): boolean {
  if (!/^[0-9]{9}$/.test(siren)) return false;
  if (siren === '356000000') return true;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const d = Number(siren[i]);
    // 1-based even positions are doubled: from the right, every second digit.
    if (i % 2 === 1) {
      const x = d * 2;
      sum += Math.floor(x / 10) + (x % 10);
    } else {
      sum += d;
    }
  }
  return sum % 10 === 0;
}

/** France's two-digit key for a SIREN: (12 + 3 × (SIREN mod 97)) mod 97. */
export const pmsFrVatKey = (siren: string): string =>
  String((12 + 3 * (Number(siren) % 97)) % 97).padStart(2, '0');

/** Mirrors `pms_vat_number_valid`: the format for every state; for FR with a
 *  numeric key, the key over a valid SIREN. A letter key is the scheme for an
 *  entity with no SIREN, so only its format is checked. */
export function pmsVatNumberValid(vat: string): boolean {
  if (!/^[A-Z]{2}[0-9A-Z]{2,13}$/.test(vat)) return false;
  if (!vat.startsWith('FR')) return true;
  if (!/^FR[0-9A-Z]{2}[0-9]{9}$/.test(vat)) return false;
  const key = vat.slice(2, 4);
  if (!/^[0-9]{2}$/.test(key)) return true;
  const siren = vat.slice(4);
  return pmsSirenValid(siren) && key === pmsFrVatKey(siren);
}

export type GuestSheetErrors = Partial<Record<keyof GuestSheetPatch, string>>;

/**
 * The fiche as it would be after `patch`, checked field by field. Returns the
 * French error per field, empty when the save may go.
 *
 * `patch` holds only what changed (`pmsGuestSheetPatch`), so an identity field
 * appearing in it on a `vegaHeld` client IS a change — refused here as the RPC
 * would refuse it.
 */
export function pmsValidateGuestSheet(sheet: GuestSheet, patch: GuestSheetPatch): GuestSheetErrors {
  const errors: GuestSheetErrors = {};
  if (sheet.vegaHeld) {
    for (const f of PMS_GUEST_IDENTITY_FIELDS) {
      if (f in patch) errors[f] = 'Tenu par Vega jusqu’à la mise en service — à corriger dans Vega';
    }
  }
  if ('lastName' in patch && (patch.lastName ?? '').trim() === '') {
    errors.lastName = 'Le nom est requis';
  }
  const siren = 'siren' in patch ? patch.siren : sheet.siren;
  if (siren != null && siren !== '' && !pmsSirenValid(pmsNormaliseSiren(siren))) {
    errors.siren = 'SIREN invalide — neuf chiffres, clé de contrôle incorrecte';
  }
  const vat = 'vatNumber' in patch ? patch.vatNumber : sheet.vatNumber;
  if (vat != null && vat !== '') {
    const v = pmsNormaliseVatNumber(vat);
    if (!pmsVatNumberValid(v)) {
      errors.vatNumber = 'N° de TVA intracommunautaire invalide';
    } else if (
      siren != null &&
      siren !== '' &&
      /^FR[0-9]{2}/.test(v) &&
      v.slice(4) !== pmsNormaliseSiren(siren)
    ) {
      errors.vatNumber = 'Le N° de TVA ne porte pas ce SIREN';
    }
  }
  const email = 'email' in patch ? patch.email : null;
  if (email != null && email.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'Adresse e-mail invalide';
  }
  const country = 'countryCode' in patch ? patch.countryCode : null;
  if (country != null && country.trim() !== '' && !/^[A-Za-z]{2}$/.test(country.trim())) {
    errors.countryCode = 'Code pays à deux lettres (FR, ES…)';
  }
  return errors;
}

/** What the desk typed into the fiche — every field as a string, the way a
 *  form holds it. */
export type GuestSheetForm = {
  [K in (typeof PMS_GUEST_IDENTITY_FIELDS)[number] | 'notes' | 'customerSubtype' | 'siren' | 'vatNumber']: string;
} & {
  lang: GuestSheet['lang'];
  customerType: GuestSheet['customerType'];
};

export function pmsGuestSheetForm(sheet: GuestSheet): GuestSheetForm {
  return {
    lastName: sheet.lastName,
    firstName: sheet.firstName,
    email: sheet.email ?? '',
    phone: sheet.phone ?? '',
    mobile: sheet.mobile ?? '',
    addressLine1: sheet.addressLine1 ?? '',
    addressLine2: sheet.addressLine2 ?? '',
    postalCode: sheet.postalCode ?? '',
    city: sheet.city ?? '',
    countryCode: sheet.countryCode ?? '',
    lang: sheet.lang,
    notes: sheet.notes ?? '',
    customerType: sheet.customerType,
    customerSubtype: sheet.customerSubtype ?? '',
    siren: sheet.siren ?? '',
    vatNumber: sheet.vatNumber ?? '',
  };
}

/**
 * The form → only what CHANGED, normalised the way the RPC stores it. An
 * untouched field is absent (unchanged); a cleared one is `null`. A client
 * switched to Particulier drops its sous-type, as the RPC does.
 *
 * Only changes are sent so that a `vegaHeld` client's identity never travels
 * at all: the screen shows it read-only, and nothing the desk did not touch
 * can be read by the database as an edit.
 */
export function pmsGuestSheetPatch(sheet: GuestSheet, form: GuestSheetForm): GuestSheetPatch {
  const patch: GuestSheetPatch = {};
  const text = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  const same = (a: string | null, b: string | null) => (a ?? null) === (b ?? null);

  if (form.lastName.trim() !== sheet.lastName.trim()) patch.lastName = form.lastName.trim();
  if (form.firstName.trim() !== sheet.firstName.trim()) patch.firstName = form.firstName.trim();
  const email = text(form.email)?.toLowerCase() ?? null;
  if (email !== (sheet.email?.toLowerCase() ?? null)) patch.email = email;
  const country = text(form.countryCode)?.toUpperCase() ?? null;
  if (!same(country, sheet.countryCode)) patch.countryCode = country;
  for (const f of ['phone', 'mobile', 'addressLine1', 'addressLine2', 'postalCode', 'city', 'notes'] as const) {
    const v = text(form[f]);
    if (!same(v, sheet[f])) patch[f] = v;
  }
  if (form.lang !== sheet.lang) patch.lang = form.lang;
  if (form.customerType !== sheet.customerType) patch.customerType = form.customerType;
  const subtype = form.customerType === 'particulier' ? null : text(form.customerSubtype);
  if (!same(subtype, sheet.customerSubtype)) patch.customerSubtype = subtype;
  const siren = text(form.siren) === null ? null : pmsNormaliseSiren(form.siren);
  if (!same(siren, sheet.siren)) patch.siren = siren;
  const vat = text(form.vatNumber) === null ? null : pmsNormaliseVatNumber(form.vatNumber);
  if (!same(vat, sheet.vatNumber)) patch.vatNumber = vat;
  return patch;
}
